import {
  columnKeyBySortMap,
  IBasketActionTypes,
} from '@epicuro-next/data/baskets';
import {
  makeBasketLineColumns,
  mapColumnsDisplayValues,
} from '@epicuro-next/data/baskets-grouping';
import { selectCurrentRoute } from '@epicuro-next/state/router-store';
import {
  selectShowWholeSalePrices,
  selectUserRoles,
} from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';
import {
  BasketColumn,
  BasketColumnKeys,
  BasketStatus,
  PriceBasketColumn,
  QuantityBasketColumn,
} from 'graphql-api/baskets-api-interfaces';
import { OrderType } from 'graphql-api/gql-api-interfaces';
import { pipe } from 'rxjs';

import {
  BasketButtonState,
  BasketLineItem,
  BasketLinesEntity,
} from '../../models';
import { BasketColumnsConfiguration } from '../../models/basket-columns-configuration';
import {
  addCurrencyToPriceColumns,
  filterColumnsByShowWholeSalesPrice,
  getFilteredBasketActions,
  hideProductLineColumnsInHeader,
  mapValueColumnByBasketStatus,
  removeUnusedColumns,
} from '../../shared';

import { selectDeliveryShops } from './basket-accounts.selectors';
import {
  selectAddressLineActionsRule,
  selectCanApplyManualDiscounts,
  selectCanUploadMdoBasketRule,
  selectCheckEveryGrossPriceForManualDiscountRule,
  selectColumnsRule,
  selectCopyProductsAllowedOrderTypesRule,
  selectManualDiscountAppliedToSelectedRule,
  selectShowBasketNotesRule,
  selectUserRolesAllowedToSeeManualDiscountsInNewishBasket,
} from './basket-configuration-rules.selectors';
import {
  selectActiveBasketId,
  selectActiveBasketTotals,
  selectAllowResendOrderConfirmation,
  selectBasketAddressGroupLines,
  selectBasketBrandGroupLines,
  selectBasketBulkTotals,
  selectBasketItems,
  selectBasketItemsWithReferenceToClosedOutProducts,
  selectBasketLineEntities,
  selectBasketLinesList,
  selectBasketOrderNumberGroupLines,
  selectBasketRealTotals,
  selectBasketRowLines,
  selectBasketStatus,
  selectBasketTotals,
  selectCanBasketBeChanged,
  selectCannotChangeBasket,
  selectCanSetDropShip,
  selectDiscountsOrderType,
  selectHasBasketNotAvailableAddress,
  selectIfSomeWithoutWsp,
  selectIsActiveBasketAndOpenedBasketDifferent,
  selectIsBasketEmptyState,
  selectIsBasketMdoView,
  selectIsBasketStatusNewish,
  selectIsBasketStatusNotNew,
  selectIsBasketStatusProcessed,
  selectIsOperableStatus,
  selectIsReadOnlyDtoBasketFlag,
} from './basket-data.selectors';
import {
  selectBasketManualDiscountIsLoadingConfig,
  selectIsManualDiscountBusy,
} from './basket-loaders.selectors';
import {
  selectBasketItemIdsEligibleForManualDiscount,
  selectHasBasketItemsEligibleForManualDiscount,
  selectHasSomeBasketManualDiscount,
} from './basket-manual-discounts.selectors';
import {
  selectBasketActions,
  selectDefaultSortConfiguration,
} from './basket-options.selectors';
import {
  isBasketStatusValidAndTotalNotZero,
  selectBackendIsNotAvailable,
  selectBasketButtonState,
  selectHasBasketLevelValidations,
  selectHasFunctionValidationError,
  selectIsBasketChecked,
  selectIsBasketStatusValid,
} from './basket-validations.selectors';
import { selectBasketUiState } from './feature.selectors';

const gridClasses = 'col-12 col-lg-9 col-xxl-9';
const gridClassesCompact = 'col-12 col-lg-8 col-xxl-9';

export const selectIsBasketCheckedOrPlaced = createSelector(
  selectIsBasketStatusValid,
  selectIsBasketStatusNotNew,
  isBasketStatusValidAndTotalNotZero,
  (
    isBasketStatusValid,
    isBasketPlaced,
    isCheckOrderButtonDisabledByValidation,
  ) => {
    return Boolean(
      (isBasketStatusValid || isBasketPlaced) &&
        !isCheckOrderButtonDisabledByValidation,
    );
  },
);

export const selectUserIsAllowedToSeeManualDiscountsForNewishBaskets =
  createSelector(
    selectUserRoles,
    selectUserRolesAllowedToSeeManualDiscountsInNewishBasket,
    (userRoles, userRolesAllowedToSeeManualDiscountsInNewishBasket) => {
      if (!userRoles || !userRolesAllowedToSeeManualDiscountsInNewishBasket) {
        return false;
      }

      const isAllowed = userRoles.some((role) =>
        userRolesAllowedToSeeManualDiscountsInNewishBasket.includes(role),
      );

      return isAllowed;
    },
  );

export const selectShouldShowManualDiscountColumns = createSelector(
  selectIsBasketStatusNewish,
  selectHasBasketItemsEligibleForManualDiscount,
  selectHasSomeBasketManualDiscount,
  selectUserIsAllowedToSeeManualDiscountsForNewishBaskets,
  selectBackendIsNotAvailable,
  selectCheckEveryGrossPriceForManualDiscountRule,
  selectIfSomeWithoutWsp,
  (
    isBasketStatusNewish,
    hasBasketItemsEligibleForManualDiscount,
    hasManualDiscounts,
    userIsAllowedToSeeManualDiscountsForNewishBaskets,
    backendNotAvailable,
    checkEveryGrossPriceForManualDiscountRule,
    ifSomeWithoutWsp,
  ) => {
    if (checkEveryGrossPriceForManualDiscountRule && ifSomeWithoutWsp) {
      return false;
    }
    return isBasketStatusNewish
      ? hasBasketItemsEligibleForManualDiscount &&
          userIsAllowedToSeeManualDiscountsForNewishBaskets &&
          !backendNotAvailable
      : hasManualDiscounts;
  },
);

export const selectColumnsByBasketState = createSelector(
  selectColumnsRule,
  selectIsBasketCheckedOrPlaced,
  selectShouldShowManualDiscountColumns,
  (columns, isBasketCheckedOrPlaced, shouldShowManualDiscountColumns) => {
    const columnSetPropertyName: keyof BasketColumnsConfiguration =
      Boolean(columns.canApplyManualDiscount) && shouldShowManualDiscountColumns
        ? 'canApplyManualDiscount'
        : 'default';

    const columnsSet = columns[columnSetPropertyName];
    if (!columnsSet) {
      throw new Error(
        `Column configuration is missing for '${columnSetPropertyName}'.`,
      );
    }

    return isBasketCheckedOrPlaced ? columnsSet.after : columnsSet.before;
  },
);

export const selectBasketColumns = createSelector(
  selectColumnsByBasketState,
  selectShowWholeSalePrices,
  (columnKeys, showWholeSalePrices) => {
    return pipe(filterColumnsByShowWholeSalesPrice(showWholeSalePrices))(
      (columnKeys || []).map(
        (key: BasketColumnKeys): BasketColumn => ({ key }),
      ),
    );
  },
);

export const selectShouldShowRrpTotals = createSelector(
  selectColumnsByBasketState,
  (columnKeys) => (columnKeys || []).includes(BasketColumnKeys.ValueRrp),
);

export const selectHasProductIdColumn = createSelector(
  selectColumnsByBasketState,
  (columnKeys) => (columnKeys || []).includes(BasketColumnKeys.ProductId),
);

export const selectExpandedBasketLines = createSelector(
  selectBasketUiState,
  (state) => state.expandedBasketLineIds,
);

export const selectSelectedBasketItemIds = createSelector(
  selectBasketUiState,
  (state) => state.selectedBasketItemIds,
);

export const selectHasSelectedBasketItems = createSelector(
  selectSelectedBasketItemIds,
  (basketItems) => Boolean(basketItems.length),
);

export const selectSelectedBasketItems = createSelector(
  selectSelectedBasketItemIds,
  selectBasketItems,
  (selectedIds, basketItems) =>
    basketItems.filter((basketItem) =>
      selectedIds.includes(basketItem.basketItemId),
    ),
);

export const selectSelectedBasketItemsIdsEligibleForManualDiscount =
  createSelector(
    selectBasketItemIdsEligibleForManualDiscount,
    selectSelectedBasketItemIds,
    selectManualDiscountAppliedToSelectedRule,
    (selectedIds, basketItemIdsEligableForManualDiscount, isRuleEnabled) => {
      if (!isRuleEnabled) return [];
      return selectedIds.filter((selectedId) =>
        basketItemIdsEligableForManualDiscount.includes(selectedId),
      );
    },
  );

export const selectRootBasketLineIds = createSelector(
  selectBasketLinesList,
  (lines) => lines.filter((line) => !line.parentId).map(({ id }) => id),
);

export const selectDefaultExpandedBasketLinesIds = createSelector(
  selectBasketOrderNumberGroupLines,
  selectBasketBrandGroupLines,
  selectBasketAddressGroupLines,
  selectIsBasketMdoView,
  (orderNumberLines, brandLines, addressLines, isMdoView) => {
    const LEVELS_COUNT_TO_EXPAND = 2;

    const nestingLevels = isMdoView
      ? [orderNumberLines, brandLines]
      : [orderNumberLines, brandLines, addressLines];

    return filterLinesToExpandFromLevels(
      nestingLevels,
      LEVELS_COUNT_TO_EXPAND,
    ).map(({ id }) => id);
  },
);

export const selectIsProductLineVisible = createSelector(
  selectBasketRowLines,
  selectBasketLineEntities,
  selectExpandedBasketLines,
  (productLine, entity, expandedLines) =>
    productLine
      .map(({ id }) => id)
      .map((id) => getParentChain(entity, id))
      .some((parentIds) =>
        parentIds.every((id) => isExpanded(expandedLines, id)),
      ),
);

export const selectHasAddressLineActions = createSelector(
  selectDeliveryShops,
  selectAddressLineActionsRule,
  selectCanBasketBeChanged,
  (basketAccounts, actions, canBeChanged) =>
    Boolean(canBeChanged && basketAccounts.length > 1 && actions.length),
);

// We restrict Now or Future items to copy
// No case when both are restricted, for this propose disallow copy with CBasketConfigurationKeys.CopyProductsEnabled rule
export const selectCopyProductsRestrictedOrderType = createSelector(
  selectCopyProductsAllowedOrderTypesRule,
  (allowedTypes) =>
    [OrderType.Future, OrderType.Now].filter(
      (type) => !allowedTypes.includes(type),
    )[0] ?? null,
);

export const selectAvailableColumns = createSelector(
  selectBasketColumns,
  selectIsBasketStatusNotNew,
  (columns, isBasketPlaced) => {
    return pipe(mapValueColumnByBasketStatus(isBasketPlaced))(columns);
  },
);

export const selectHasManualDiscountColumn = createSelector(
  selectAvailableColumns,
  (basketColumns) => {
    return basketColumns.some(
      (bc) => bc.key === BasketColumnKeys.ManualDiscount,
    );
  },
);

const selectBasketFooterColumnsData = createSelector(
  selectBasketRealTotals,
  selectAvailableColumns,
  selectHasSomeBasketManualDiscount,
  selectDiscountsOrderType,
  (
    realTotals,
    availableColumns,
    hasSomeBasketManualDiscount,
    discountOrderType,
  ) =>
    removeUnusedColumns(
      availableColumns,
      makeBasketLineColumns({
        total: realTotals,
        isManualDiscountApplied: hasSomeBasketManualDiscount,
        orderType: discountOrderType,
      }),
    ),
);

export const selectBasketFooterColumns = createSelector(
  selectBasketFooterColumnsData,
  selectIsBasketChecked,
  selectBackendIsNotAvailable,
  selectIsBasketStatusNotNew,
  (footerColumns, basketIsChecked, backendNotAvailable, showConfirmedValues) =>
    mapColumnsDisplayValues(
      footerColumns,
      basketIsChecked,
      backendNotAvailable,
      showConfirmedValues,
    ),
);

export const selectBasketBulkFooterColumnsData = createSelector(
  selectBasketBulkTotals,
  selectAvailableColumns,
  selectHasSomeBasketManualDiscount,
  (bulkTotals, availableColumns, hasSomeBasketManualDiscount) =>
    removeUnusedColumns(
      availableColumns,
      makeBasketLineColumns({
        total: bulkTotals,
        isManualDiscountApplied: hasSomeBasketManualDiscount,
      }),
    ),
);

export const selectBasketBulkFooterColumns = createSelector(
  selectBasketBulkFooterColumnsData,
  selectIsBasketChecked,
  selectBackendIsNotAvailable,
  selectIsBasketStatusNotNew,
  (bulkColumns, basketIsChecked, backendNotAvailable, showConfirmedValues) =>
    mapColumnsDisplayValues(
      bulkColumns,
      basketIsChecked,
      backendNotAvailable,
      showConfirmedValues,
    ),
);

export const selectActiveBasketColumns = createSelector(
  selectActiveBasketTotals,
  selectHasSomeBasketManualDiscount,
  (totals, hasSomeBasketManualDiscount) =>
    totals
      ? makeBasketLineColumns({
          total: totals,
          isManualDiscountApplied: hasSomeBasketManualDiscount,
        })
      : [],
);

export const selectActiveBasketQuantity = createSelector(
  selectActiveBasketColumns,
  (columns) =>
    (columns &&
      (
        columns.find((column) => column.key === BasketColumnKeys.Quantity) as
          | QuantityBasketColumn
          | undefined
      )?.value) ||
    0,
);

export const selectActiveBasketValue = createSelector(
  selectActiveBasketColumns,
  selectBasketColumns,
  (columns, availableColumns) =>
    (
      removeUnusedColumns(availableColumns, columns).find((column) =>
        [
          BasketColumnKeys.Value,
          BasketColumnKeys.ValueRrp,
          BasketColumnKeys.ValueNet,
          BasketColumnKeys.Total,
        ].includes(column.key),
      ) as PriceBasketColumn | undefined
    )?.value || 0,
);

export const selectActiveBasketDisplayedCurrency = createSelector(
  selectActiveBasketColumns,
  selectBasketColumns,
  (columns, availableColumns) =>
    (
      removeUnusedColumns(availableColumns, columns).find((column) =>
        [
          BasketColumnKeys.Value,
          BasketColumnKeys.ValueRrp,
          BasketColumnKeys.ValueNet,
          BasketColumnKeys.Total,
        ].includes(column.key),
      ) as PriceBasketColumn | undefined
    )?.currency || '',
);

export const selectIsDefaultSortingColumnPresent = createSelector(
  selectDefaultSortConfiguration,
  selectAvailableColumns,
  (defaultSorting, columns) =>
    columns.some(({ key }) => columnKeyBySortMap[defaultSorting.type] === key),
);

export const selectHasSortingThirdState = createSelector(
  selectIsDefaultSortingColumnPresent,
  (isDefaultSortingColumnPresent) => !isDefaultSortingColumnPresent,
);

export const selectIsMakeBasketActiveEnabled = createSelector(
  selectIsActiveBasketAndOpenedBasketDifferent,
  selectCanBasketBeChanged,
  (isDifferent, canBeActive) => isDifferent && canBeActive,
);

export const selectIsCheckOrderButtonDisabled = createSelector(
  selectHasFunctionValidationError,
  isBasketStatusValidAndTotalNotZero,
  selectIsOperableStatus,
  selectBasketButtonState,
  selectHasBasketNotAvailableAddress,
  selectIsManualDiscountBusy,
  selectIsReadOnlyDtoBasketFlag,
  (
    hasFuncValidationErrors,
    disabledByValidation,
    isOperable,
    basketButtonState,
    hasDeletedAddress,
    isManualDiscountUpdating,
    isReadOnly,
  ) =>
    hasFuncValidationErrors ||
    disabledByValidation ||
    !isOperable ||
    hasDeletedAddress ||
    basketButtonState === BasketButtonState.WaitingForReview ||
    isManualDiscountUpdating ||
    isReadOnly,
);

export const selectShowBasketNotes = createSelector(
  selectShowBasketNotesRule,
  selectBasketStatus,
  selectIsBasketChecked,
  (ruleEnabled, status, isChecked) => {
    return ruleEnabled && (status !== BasketStatus.New || isChecked);
  },
);

export const selectCanApplyManualDiscount = createSelector(
  selectCanApplyManualDiscounts,
  selectCanBasketBeChanged,
  selectHasBasketItemsEligibleForManualDiscount,
  selectHasManualDiscountColumn,
  selectBackendIsNotAvailable,
  (
    canApplyManualDiscounts,
    canBasketBeChanged,
    hasBasketItemsEligibleForManualDiscount,
    hasManualDiscountColumn,
    backendNotAvailable,
  ) => {
    return (
      canApplyManualDiscounts &&
      canBasketBeChanged &&
      hasBasketItemsEligibleForManualDiscount &&
      hasManualDiscountColumn &&
      !backendNotAvailable
    );
  },
);

export const selectAvailableColumnsEnrichedWithPrice = createSelector(
  selectAvailableColumns,
  selectBasketTotals,
  addCurrencyToPriceColumns,
);

export const selectHeaderColumns = createSelector(
  selectAvailableColumnsEnrichedWithPrice,
  selectIsProductLineVisible,
  (availableColumns, isProductLineVisible) => {
    return isProductLineVisible
      ? availableColumns
      : hideProductLineColumnsInHeader(availableColumns);
  },
);

export const selectColumnsCount = createSelector(
  selectHeaderColumns,
  (columns) => columns.length,
);

export const selectCompactView = createSelector(
  selectColumnsCount,
  (length) => {
    return length > 6;
  },
);

export const selectGridClasses = createSelector(
  selectCompactView,
  (compactView) => (compactView ? gridClassesCompact : gridClasses),
);

export const selectAnyCloseOutBasketItems = createSelector(
  selectBasketItemsWithReferenceToClosedOutProducts,
  (closeOutBasketItems) =>
    closeOutBasketItems.filter(
      (basketItem) => basketItem.orderType !== OrderType.Future,
    ).length > 0,
);

export const selectShowClearDiscountsButton = createSelector(
  selectCanApplyManualDiscount,
  selectHasSomeBasketManualDiscount,
  (shouldShowManualDiscount, hasSomeManualDiscount) =>
    shouldShowManualDiscount && hasSomeManualDiscount,
);

export const selectBasketStateInfo = createSelector(
  selectIsBasketEmptyState,
  selectCannotChangeBasket,
  selectIsBasketStatusProcessed,
  (isEmpty, cannotBeChanged, isProcessed) => {
    return {
      isEmpty,
      cannotBeChanged,
      isProcessed,
    };
  },
);

export const selectFilteredBasketActions = createSelector(
  selectBasketActions,
  selectCanUploadMdoBasketRule,
  selectCanSetDropShip,
  selectBasketStateInfo,
  selectAllowResendOrderConfirmation,
  selectShowClearDiscountsButton,
  (
    actions,
    allowMDOUpload,
    canSetDropShip,
    basketStateInfo,
    isAllowedToResendOrderConfirmation,
    showClearDiscountsButton,
  ) => {
    const filteredActions = getFilteredBasketActions(
      actions,
      isAllowedToResendOrderConfirmation,
      basketStateInfo.isProcessed,
      canSetDropShip,
      allowMDOUpload ?? false,
      showClearDiscountsButton,
    );

    if (basketStateInfo.isEmpty) {
      const allowedTypes = [
        IBasketActionTypes.BASKET_MANAGER,
        IBasketActionTypes.EDIT_ADDRESSES,
      ];
      return filteredActions.filter(({ actionType }) =>
        allowedTypes.includes(actionType),
      );
    }

    if (basketStateInfo.cannotBeChanged) {
      const allowedTypes = [
        IBasketActionTypes.SWITCH_BASKET,
        IBasketActionTypes.BASKET_MANAGER,
        IBasketActionTypes.DOWNLOAD_CONTENT,
        IBasketActionTypes.DOWNLOAD_CONTENT_H2,
        IBasketActionTypes.DOWNLOAD_PDF_ASYNC,
        IBasketActionTypes.UPLOAD_MDO_BASKET,
      ];

      if (basketStateInfo.isProcessed && isAllowedToResendOrderConfirmation) {
        allowedTypes.push(IBasketActionTypes.SEND_PDF_CONFIRMATION);
      }

      return filteredActions.filter(({ actionType }) =>
        allowedTypes.includes(actionType),
      );
    }

    return filteredActions.reverse();
  },
);

export const selectApplyingManualDiscountsIsDisabled = createSelector(
  selectHasBasketLevelValidations,
  selectBasketManualDiscountIsLoadingConfig,
  (hasBasketLevelValidations, basketManualDiscountIsLoadingConfig) =>
    hasBasketLevelValidations || basketManualDiscountIsLoadingConfig,
);

export const getParentChain = (
  entity: BasketLinesEntity,
  basketLineId: string,
): string[] => {
  const parentId = entity[basketLineId].parentId;
  return !parentId ? [] : [parentId, ...getParentChain(entity, parentId)];
};
export const isExpanded = (expandedLines: string[], id: string) =>
  expandedLines.includes(id);

function filterLinesToExpandFromLevels(
  nestingLevels: BasketLineItem[][],
  levelsCount: number,
) {
  return nestingLevels
    .filter((levelLinesArray) => levelLinesArray.length)
    .slice(0, levelsCount)
    .flat();
}

export const selectCurrentRouteIsActiveBasket = createSelector(
  selectCurrentRoute,
  selectActiveBasketId,
  (currentRoute, activeBasketId) => {
    const currentRouteIsActiveBasket =
      currentRoute?.params.basketId === activeBasketId &&
      currentRoute.routeConfig?.path === ':basketId';
    return currentRouteIsActiveBasket;
  },
);
